import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Grid,
    Typography,
    Chip,
    IconButton,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper
} from '@mui/material';
import { Close as CloseIcon, Biotech as BiotechIcon } from '@mui/icons-material';

const SampleDetail = ({ sample, onClose }) => {
    if (!sample) return null;

    const {
        sampleName,
        controlType,
        lotNumber,
        lotExpiration,
        analytes = [],
        qcRanges = []
    } = sample;

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString();
    };

    const renderField = (label, value) => (
        <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary" display="block">
                {label}
            </Typography>
            <Typography variant="body1">
                {value || 'N/A'}
            </Typography>
        </Grid>
    );

    return (
        <Dialog
            open={true}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{ sx: { minHeight: '50vh' } }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BiotechIcon color="primary" />
                    <Typography variant="h6" component="span">
                        {sampleName}
                    </Typography>
                    {controlType && (
                        <Chip label={controlType} size="small" variant="outlined" />
                    )}
                </Box>
                <IconButton onClick={onClose} size="small" aria-label="close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {/* Sample Info */}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Sample Information
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={2}>
                        {renderField('Sample Name', sampleName)}
                        {renderField('Control Type', controlType)}
                        {renderField('Lot Number', lotNumber)}
                        {renderField('Lot Expiration', formatDate(lotExpiration))}
                    </Grid>
                </Paper>

                {/* Analytes */}
                {analytes.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                            Analytes ({analytes.length})
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {analytes.map((a, idx) => (
                                <Chip key={idx} label={a} size="small" color="primary" variant="outlined" />
                            ))}
                        </Box>
                    </Box>
                )}

                {/* QC Ranges Table */}
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    QC Ranges ({qcRanges.length})
                </Typography>

                {qcRanges.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No QC ranges found for this sample.
                    </Typography>
                ) : (
                    <Paper variant="outlined" sx={{ overflow: 'auto' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Range Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Analyte</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Control Type</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Lot Number</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="right">Target Value</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="right">SD</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="right">-10% Range</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="right">+10% Range</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="right">Min Valid</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align="right">Max Valid</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {qcRanges.map((r, idx) => (
                                    <TableRow
                                        key={r.legacyRangeId || idx}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell>{r.rangeName || '-'}</TableCell>
                                        <TableCell>{r.analyte || '-'}</TableCell>
                                        <TableCell>{r.controlType || '-'}</TableCell>
                                        <TableCell>{r.lotNumber || '-'}</TableCell>
                                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{r.targetValue || '-'}</TableCell>
                                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{r.sd || '-'}</TableCell>
                                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{r.minus10PctRange || '-'}</TableCell>
                                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{r.plus10PctRange || '-'}</TableCell>
                                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{r.minValidRange || '-'}</TableCell>
                                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{r.maxValidRange || '-'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default SampleDetail;
